<template>
  <div>
    <FocusIndicator
      card-selector=".b-table:not(.gid-table-infinite) tbody tr"
      :name-extractor="extractPartnerName"
    />
    <b-card bg-variant="light" class="my-3">
      <b-row>
        <b-col md="3">
          <b-form-group label-cols="3" :label="`${$t('filter')}:`" class="mb-0">
            <b-input-group>
              <b-form-input
                v-model="filterSearch"
                :placeholder="$t('type-to-search')"
                debounce="300"
              />
              <b-input-group-append v-if="filterSearch">
                <b-btn @click="filterSearch = ''">&times;</b-btn>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label-cols="2" :label="`${$t('status')}:`" class="mb-0">
            <div class="d-flex align-items-stretch">
              <multiselect
                v-model="filterStatuses"
                :options="accountStatusOptions"
                :multiple="true"
                label="text"
                track-by="value"
                :placeholder="$t('select-statuses')"
                class="flex-grow-1"
                @input="onStatusSelectionChange"
              />
              <b-button-group class="ml-2 status-button-group">
                <b-button
                  :variant="
                    isActiveStatusesSelected ? 'primary' : 'outline-primary'
                  "
                  @click="selectActiveStatuses"
                  :title="$t('select-active-statuses')"
                  class="status-button"
                >
                  <i class="material-icons">check_circle</i>
                </b-button>
                <b-button
                  variant="outline-secondary"
                  @click="clearAllStatuses"
                  :title="$t('clear-all-statuses')"
                  class="status-button"
                >
                  <i class="material-icons">clear</i>
                </b-button>
              </b-button-group>
            </div>
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label-cols="3" :label="`${$t('owner')}:`" class="mb-0">
            <b-form-select v-model="filterOwner" :options="owners">
              <template slot="first">
                <option :value="null">-- {{ $t('all') }} --</option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="2" class="pt-2">
          <b-form-checkbox v-model="filterParentsOnly">
            {{ $t('filters.parents-only') }}
          </b-form-checkbox>
          <b-form-checkbox v-model="filterMissingTaxInfo">
            {{ $t('filters.missing-tax-info') }}
          </b-form-checkbox>
          <b-form-checkbox v-model="filterUseAdvancedSearch">
            {{ $t('filters.advanced.toggle') }}
          </b-form-checkbox>
        </b-col>
      </b-row>
      <advanced-search
        v-if="filterUseAdvancedSearch"
        v-model="filterAdvancedSearch"
        :filter-options="advancedFilterOptions"
      />
    </b-card>
    <b-pagination
      v-if="totalRows > perPage"
      :total-rows="totalRows"
      :per-page="perPage"
      v-model="currentPage"
      align="center"
    />
    <b-table
      show-empty
      striped
      stacked="md"
      :items="items"
      :busy="loading"
      :fields="tableFields"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDir"
      no-local-sorting
    >
      <template #empty>
        <p class="text-muted text-center m-3">
          {{ $t('list-no-results-filter-hint') }}
        </p>
      </template>
      <template #thead-top>
        <tr>
          <th colspan="2">{{ $t('partners-table.partner') }}</th>
          <th colspan="7" class="border-left">
            {{ $t('partners-table.jobs') }}
          </th>
          <th colspan="2" class="border-left">
            {{ $t('partners-table.actions') }}
          </th>
          <th>&nbsp;</th>
          <th>&nbsp;</th>
        </tr>
      </template>
      <template #cell(account.name)="{ item }">
        <p>
          {{ item.account.name }}
          <b-badge v-if="item.is_parent_account" variant="info">
            {{ $t('partner.has-children') }}
          </b-badge>
          <b-form-checkbox
            v-if="item.is_parent_account"
            :checked="!!includeChildrenMap[item.account.id]"
            @change="toggleIncludeChildren(item)"
            class="d-inline-block ml-1"
            size="sm"
          >
            <small>{{ $t('filters.include-children-jobs') }}</small>
          </b-form-checkbox>
          <b-badge v-if="item.is_partner_paused" variant="warning">
            {{ $t('partner-pause-generic.paused') }}
          </b-badge>
          <b-badge
            v-if="isMissingTaxInfo(item)"
            variant="danger"
            :title="$t('partner.tax.missing-tooltip')"
          >
            {{ $t('partner.tax.missing-badge') }}
          </b-badge>
          <b-badge
            v-if="pendingPriceCounts[item.account.id]"
            variant="warning"
            class="ml-1"
          >
            {{
              $tc(
                'partner-prices.pending-count',
                pendingPriceCounts[item.account.id],
                { count: pendingPriceCounts[item.account.id] },
              )
            }}
          </b-badge>
          &nbsp;
          <a :href="dataUIAccountURL(item.account.id)" target="_blank">
            <i class="material-icons">edit</i>
          </a>
          <br />
          {{ item.account.shipping_address?.street }}
          <br />
          {{ item.account.shipping_address?.country_code }} -
          {{ item.account.shipping_address?.postal_code }}
          {{ item.account.shipping_address?.city }}
          <br />
          <template v-if="item.account.website">
            <a :href="websiteURL(item.account.website)" target="_blank">
              {{ item.account.website }}
            </a>
          </template>
          <br />
          <b-badge
            class="ml-1"
            :variant="partnerExperienceLevel(item.completed_jobs).variant"
          >
            {{ partnerExperienceLevel(item.completed_jobs).text }}
          </b-badge>
        </p>
        <table class="table table-borderless small w-auto">
          <tr class="bg-transparent">
            <td class="pl-0 py-1 pr-2">{{ $t('partner.all-ratings') }}</td>
            <td class="p-0 py-1">
              <RatingWidget
                context="job"
                :count="item.rating_count ?? 0"
                :average="item.rating_avg"
              />
            </td>
          </tr>
          <tr class="bg-transparent">
            <td class="pl-0 py-1 pr-2">{{ $t('partner.last-rating') }}</td>
            <td class="p-0 py-1">
              <RatingWidget context="job" :average="item.rating_last" />
            </td>
          </tr>
        </table>
      </template>
      <template #cell(revenue)="{ item, value }">
        <b-spinner v-if="loadingChildrenMap[item.account.id]" small />
        <template v-else>{{ value | currency }}</template>
      </template>
      <template #cell(relative_margin)="{ item }">
        <b-spinner v-if="loadingChildrenMap[item.account.id]" small />
        <template v-else>
          <span v-if="item.relative_margin < 0" class="text-danger">
            {{ $t('negative-margin') }}
          </span>
          <span v-else>
            {{
              item.relative_margin ? `${item.relative_margin.toFixed(2)}%` : 0
            }}
          </span>
        </template>
      </template>
      <template #cell(absolute_margin)="{ item }">
        <b-spinner v-if="loadingChildrenMap[item.account.id]" small />
        <template v-else>{{ item.absolute_margin || 0 | currency }}</template>
      </template>
      <template #cell(shown_jobs)="{ item, value }">
        <b-spinner v-if="loadingChildrenMap[item.account.id]" small />
        <b-link v-else @click="onCallResolved(item, 'shown')">
          {{ value }}
        </b-link>
      </template>
      <template #cell(potential_jobs)="{ item, value }">
        <b-spinner v-if="loadingChildrenMap[item.account.id]" small />
        <b-link v-else @click="onCallResolved(item, 'potential')">
          {{ value }}
        </b-link>
      </template>
      <template #cell(active_jobs)="{ item, value }">
        <b-spinner v-if="loadingChildrenMap[item.account.id]" small />
        <b-link v-else @click="onCallResolved(item, 'active')">
          {{ value }}
        </b-link>
      </template>
      <template #cell(completed_jobs)="{ item, value }">
        <b-spinner v-if="loadingChildrenMap[item.account.id]" small />
        <b-link v-else @click="onCallResolved(item, 'completed')">
          {{ value }}
        </b-link>
      </template>
      <template #cell(rejected_jobs)="{ item, value }">
        <b-spinner v-if="loadingChildrenMap[item.account.id]" small />
        <b-link v-else @click="onCallResolved(item, 'rejected')">
          {{ value }}
        </b-link>
      </template>
      <template #cell(cancelled_jobs)="{ item, value }">
        <b-spinner v-if="loadingChildrenMap[item.account.id]" small />
        <b-link v-else @click="onCallResolved(item, 'cancelled')">
          {{ value }}
        </b-link>
      </template>
      <template #cell(rainy_potential_jobs)="{ item, value }">
        <b-spinner v-if="loadingChildrenMap[item.account.id]" small />
        <b-link v-else @click="onCallResolved(item, 'rainy-potential')">
          {{ value }}
        </b-link>
      </template>
      <template #cell(_actions)="{ item }">
        <b-row class="mx-0 mb-1">
          <b-button
            size="sm"
            variant="outline-primary"
            @click="onCallResolved(item)"
          >
            <div class="contact-partner-btn-content">
              <span class="material-icons left-icon">perm_phone_msg</span>
              <span>{{ $t('partner-contact-modal') }}</span>
            </div>
          </b-button>
        </b-row>
        <b-dropdown
          size="lg"
          variant="link"
          toggle-class="text-decoration-none p-0 text-dark"
          menu-class="menu-styling"
          no-caret
          right
        >
          <template #button-content>
            <span
              :class="`${
                isPartnerWatching(item)
                  ? 'material-icons'
                  : 'material-icons-outlined'
              } cursor-pointer heading-2`"
            >
              visibility
            </span>
          </template>
          <div class="d-block">
            <b-dropdown-item-button
              variant="outline-primary"
              class="text-left"
              aria-describedby="dropdown-header-2"
              @click="toggleWatching(item)"
            >
              {{
                isPartnerWatching(item)
                  ? $t('watchlist.stop-watching')
                  : $t('watchlist.start-watching')
              }}
            </b-dropdown-item-button>
            <b-dropdown-divider></b-dropdown-divider>
            <b-row>
              <b-col>
                <span class="heading-3 font-weight-bold">
                  {{
                    item.watchlist.length === 0
                      ? $t('watchlist.no-watchers')
                      : $t('watchlist.watchers')
                  }}
                </span>
              </b-col>
            </b-row>
            <template>
              <b-row v-for="(user, index) in item.watchlist" :key="index">
                <b-col cols="10" class="text-ellipsis">
                  <span class="heading-3">{{ user.name }}</span>
                </b-col>
                <b-col cols="2" class="text-right">
                  <span
                    class="material-icons cursor-pointer heading-3"
                    @click="unWatchPartner(item, user.sfid)"
                  >
                    close
                  </span>
                </b-col>
              </b-row>
            </template>
          </div>
        </b-dropdown>
      </template>
      <template
        #cell(_details)="{ toggleDetails, detailsShowing }"
        colspan="100%"
      >
        <button
          @click.stop="toggleDetails"
          style="font-size: 1.9rem; background: transparent; border: 0"
          class="material-icons"
        >
          {{ detailsShowing ? 'expand_less' : 'expand_more' }}
        </button>
      </template>
      <template #row-details="{ item }">
        <partner-details
          :partner="item"
          :includeChildren="!!includeChildrenMap[item.account.id]"
          @update="loadItems"
        />
      </template>
    </b-table>
    <b-alert v-if="error !== null" variant="danger" class="text-left" show>
      <h4 class="alert-heading">{{ $t('_errors.network.title') }}</h4>
      <p>{{ error.message }}</p>
      <b-collapse id="error-details" class="my-2">
        <b-card>
          <pre>{{ error.details }}</pre>
        </b-card>
      </b-collapse>
      <b-btn v-b-toggle.error-details>
        {{ $t('login.buttons.error-details') }}
      </b-btn>
    </b-alert>
    <b-pagination
      :total-rows="totalRows"
      :per-page="perPage"
      v-model="currentPage"
      align="center"
    />
    <div v-if="currentPageCount" class="text-center text-muted">
      <small>{{ currentPageCount }} / {{ totalRows }}</small>
    </div>
    <!--Partner Jobs Modal & Partner Reached Modal -->
    <PartnerJobsView
      :show="showJobPartnerModal"
      :activeTab="tabToShow"
      :partner="jobPartner"
      :includeChildren="!!includeChildrenMap[jobPartner.id]"
      @close="onCallEnded"
    />
  </div>
</template>

<script>
import PartnerJobsView from '@/components/partner/PartnerJobsView.vue';
import {
  SET_PARTNERS_FILTER,
  SET_PARTNERS_DATA,
} from '@/store/partners.module';
import { CondOperator, RequestQueryBuilder } from '@dataui/crud-request';
import {
  AccountStatusEnum,
  ContactProfileEnum,
  PartnerExperienceEnum,
} from '@gid/models';
import Api from '@gid/vue-common/api';
import AdvancedSearch from '@gid/vue-common/components/filters/AdvancedSearch.vue';
import FocusIndicator from '@gid/vue-common/components/FocusIndicator.vue';
import Multiselect from 'vue-multiselect';
import { mapFilters } from '@gid/vue-common/store/utils';
import { normalizeSearchInput } from '@gid/vue-common/utils';
import { mapMutations } from 'vuex';
import {
  _partnerExperienceLevel,
  getPartnerExperienceRange,
} from '@gid/vue-common/utils';
import axios from 'axios';
import Vue from 'vue';
import PartnerDetails from './PartnerDetails.vue';
import PartnerWatcherFilter from './PartnerWatcherFilter.vue';
import RatingFilter from './RatingFilter.vue';
import RatingWidget from './RatingWidget.vue';

Vue.component('rating-filter', RatingFilter);
Vue.component('partner-watcher-filter', PartnerWatcherFilter);

export default {
  components: {
    AdvancedSearch,
    FocusIndicator,
    Multiselect,
    PartnerDetails,
    PartnerJobsView,
    RatingWidget,
  },
  data() {
    return {
      activeStatuses: [
        AccountStatusEnum.ACTIVE,
        AccountStatusEnum.BEGINNER_ONBOARDING,
        AccountStatusEnum.REPEATED_ONBOARDING,
      ],
      isActiveMode: false,
      accountStatusOptions: Object.values(AccountStatusEnum).map((status) => ({
        text: status,
        value: status,
      })),
      filterParentsOnly: false,
      filterMissingTaxInfo: false,
      pendingPriceCounts: {},
      items: [],
      currentPage: 1,
      currentPageCount: null,
      perPage: 10,
      totalRows: 0,
      sortBy: null,
      sortDir: false,
      loading: false,
      tabToShow: 'shown',
      owners: [],
      error: null,
      advancedFilterOptions: [
        {
          label: 'Location',
          type: 'point_with_radius',
          component: 'geo',
        },
        {
          type: 'hwo_a',
          label: 'HwO A',
          picklistName: 'HwO_A',
          component: 'CrudPicklistMultiSelect',
          multiple: true,
        },
        {
          type: 'hwo_b1',
          label: 'HwO B1',
          picklistName: 'HwO_B1',
          component: 'CrudPicklistMultiSelect',
          multiple: true,
        },
        {
          type: 'hwo_b2',
          label: 'HwO B2',
          picklistName: 'HwO_B2',
          component: 'CrudPicklistMultiSelect',
          multiple: true,
        },
        {
          type: 'kein_hwo',
          label: 'Kein HwO',
          picklistName: 'nicht_HwO',
          component: 'CrudPicklistMultiSelect',
          multiple: true,
        },
        {
          type: 'zusatzqualifikation',
          label: 'Zusatzqualifikation',
          picklistName: 'Zertifikat_Zusatzqualifikation',
          component: 'CrudPicklistMultiSelect',
          multiple: true,
        },
        {
          type: 'weiterbildung',
          label: 'Brand Training',
          picklistName: 'Weiterbildung',
          component: 'CrudPicklistMultiSelect',
          multiple: true,
        },
        {
          type: 'transporter',
          label: 'Transporter',
          picklistName: 'Transporter',
          component: 'CrudPicklistMultiSelect',
          multiple: true,
        },
        {
          label: this.$i18n.t('filters.partner-experience'),
          type: 'partner-experience',
          component: 'filter-multiselect',
          values: Object.values(PartnerExperienceEnum).map((value) => {
            const [lowerRange, upperRange] = getPartnerExperienceRange(value);
            return {
              text: value,
              value: JSON.stringify({
                [`partner-experience_from`]: lowerRange,
                [`partner-experience_to`]: upperRange,
              }),
            };
          }),
          multiple: false,
        },
        {
          label: 'Partner Average Rating',
          type: 'rating_avg',
          component: 'rating-filter',
        },
        {
          type: 'watcher',
          label: 'Watched Partners',
          component: 'partner-watcher-filter',
          multiple: true,
        },
      ],
      showJobPartnerModal: false,
      jobPartner: {},
      skipWatcher: false,
      includeChildrenMap: {},
      loadingChildrenMap: {},
      originalMetrics: {},
    };
  },
  computed: {
    tableFields() {
      return [
        {
          label: '',
          key: 'account.name',
        },
        {
          label: this.$t('partner.revenue'),
          key: 'revenue',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('partner.relative-margin'),
          key: 'relative_margin',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('partner.absolute-margin'),
          key: 'absolute_margin',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('job.tabs.shown'),
          key: 'shown_jobs',
          thClass: 'border-left',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('job.tabs.potential'),
          key: 'potential_jobs',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('job.tabs.active'),
          key: 'active_jobs',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('job.tabs.done'),
          key: 'completed_jobs',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('job.tabs.rejected'),
          key: 'rejected_jobs',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('job.tabs.cancelled'),
          key: 'cancelled_jobs',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('job.tabs.rainy-potential'),
          key: 'rainy_potential_jobs',
          thClass: 'border-right',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: '',
          key: '_actions',
          class: 'text-left',
        },
        {
          label: '',
          key: '_details',
          class: 'text-left',
          tdClass: 'align-middle',
          tdAttr: { colspan: '100%' },
        },
      ];
    },
    ...mapFilters({
      moduleName: 'partners',
      states: [
        'search',
        'statuses',
        'owner',
        'useAdvancedSearch',
        'advancedSearch',
        'isActiveMode',
      ],
      setMutation: SET_PARTNERS_FILTER,
      setCallback: 'null',
    }),
    null() {
      return null;
    },
    dataUIAccountURL() {
      return (accountId) => {
        return `./data-ui/Account/${accountId}`;
      };
    },
    partnerExperienceLevel() {
      return _partnerExperienceLevel;
    },
    websiteURL() {
      return (website) => {
        if (website.startsWith('http://') || website.startsWith('https://')) {
          return website;
        }
        return `https://${website}`;
      };
    },
    isActiveStatusesSelected() {
      return this.isActiveMode;
    },
    isMissingTaxInfo() {
      // Mirrors the admin credit-note rule (accounting-drafts): a partner is
      // missing tax info only when BOTH VAT id and tax number are empty.
      return (item) => {
        const account = item?.account || {};
        const hasVatId = !!account.vatId && `${account.vatId}`.trim() !== '';
        const hasTaxId = !!account.taxId && `${account.taxId}`.trim() !== '';
        return !hasVatId && !hasTaxId;
      };
    },
  },
  watch: {
    currentPage() {
      clearTimeout(this.$_timeout);
      this.$_timeout = setTimeout(() => {
        this.loadItems();
      }, 250);
    },
    sortBy() {
      this.loadItems();
    },
    sortDir() {
      this.loadItems();
    },
    filterSearch(nextVal, prevVal) {
      if (this.skipWatcher) return;
      if (
        (normalizeSearchInput(prevVal) !== normalizeSearchInput(nextVal) &&
          nextVal.length > 1) ||
        nextVal.length == 0
      ) {
        this.filterUpdated();
      }
    },
    filterAdvancedSearch(nextVal, prevVal) {
      if (JSON.stringify(prevVal) !== JSON.stringify(nextVal)) {
        this.filterUpdated();
      }
    },
    filterStatuses(nextVal, prevVal) {
      if (JSON.stringify(prevVal) !== JSON.stringify(nextVal)) {
        this.filterUpdated();
      }
    },
    isActiveMode(nextVal, prevVal) {
      if (prevVal !== nextVal) {
        this.filterUpdated();
      }
    },
    filterOwner(nextVal, prevVal) {
      if (prevVal !== nextVal) {
        this.filterUpdated();
      }
    },
    filterParentsOnly(nextVal, prevVal) {
      if (prevVal !== nextVal) {
        this.filterUpdated();
      }
    },
    filterMissingTaxInfo(nextVal, prevVal) {
      if (prevVal !== nextVal) {
        this.filterUpdated();
      }
    },
  },
  created() {
    this.$_timeout = null;

    // Set active mode only if no statuses are manually selected
    if (!this.filterStatuses || this.filterStatuses.length === 0) {
      this.isActiveMode = true;
      this.filterStatuses = this.activeStatuses.map((status) => ({
        text: status,
        value: status,
      }));
    }

    this.loadItems();

    axios.get('/data-api/account-service/pending-counts').then((response) => {
      this.pendingPriceCounts = response.data ?? {};
    });

    const gidAccountId = 1;
    const queryString = RequestQueryBuilder.create({
      filter: [
        {
          field: 'accountId',
          operator: CondOperator.EQUALS,
          value: gidAccountId,
        },
      ],
      fields: ['id', 'name', 'isDeleted', 'profiles'],
      sort: [{ field: 'name', order: 'ASC' }],
    }).query();

    axios.get(`/data-api/contact?${queryString}`).then((response) => {
      if (response.data.length) {
        this.owners = response.data
          .filter((record) => !record.isDeleted)
          .filter((record) =>
            record.profiles.some(
              ({ profile }) =>
                profile === ContactProfileEnum.PARTNER_MANAGEMENT,
            ),
          )
          .map(({ id, name }) => ({
            value: id,
            text: name,
          }));
      }
    });
  },
  beforeDestroy() {
    // Ensure that any pending timeout is cleared on destroy
    clearTimeout(this.$_timeout);
  },
  methods: {
    ...mapMutations([SET_PARTNERS_DATA]),
    async toggleIncludeChildren(item) {
      const accountId = item.account.id;

      // Guard against concurrent requests for the same account
      if (this.loadingChildrenMap[accountId]) return;

      const wasEnabled = !!this.includeChildrenMap[accountId];

      if (wasEnabled) {
        // Restore original metrics
        this.$set(this.includeChildrenMap, accountId, false);
        const original = this.originalMetrics[accountId];
        if (original) {
          for (const [key, value] of Object.entries(original)) {
            this.$set(item, key, value);
          }
          delete this.originalMetrics[accountId];
        }
        return;
      }

      // Save original metrics before overwriting
      this.originalMetrics[accountId] = {
        revenue: item.revenue,
        relative_margin: item.relative_margin,
        absolute_margin: item.absolute_margin,
        shown_jobs: item.shown_jobs,
        potential_jobs: item.potential_jobs,
        active_jobs: item.active_jobs,
        completed_jobs: item.completed_jobs,
        rejected_jobs: item.rejected_jobs,
        cancelled_jobs: item.cancelled_jobs,
        rainy_potential_jobs: item.rainy_potential_jobs,
      };

      this.$set(this.includeChildrenMap, accountId, true);
      this.$set(this.loadingChildrenMap, accountId, true);

      // Fetch all children partners' metrics in one API call
      const childIds = item.child_account_ids || [];
      if (childIds.length === 0) {
        this.$set(this.loadingChildrenMap, accountId, false);
        return;
      }

      const allIds = [accountId, ...childIds];
      const searchJson = JSON.stringify({
        and: [
          {
            field: 'account_ids',
            op: 'includes',
            value: allIds,
          },
        ],
      });

      // Fire all requests independently — update UI as each resolves.
      // All job counts come from gid-crud (live data, consistent with modal).
      // Revenue/margins come from partner records (gid-api).
      const jobEndpoints = {
        'shown-jobs': 'shown_jobs',
        'potential-jobs': 'potential_jobs',
        'active-jobs': 'active_jobs',
        'completed-jobs': 'completed_jobs',
        'cancelled-jobs': 'cancelled_jobs',
        'rejected-jobs': 'rejected_jobs',
      };
      let pending = Object.keys(jobEndpoints).length + 1; // +1 for partner records
      const done = () => {
        pending--;
        if (pending === 0) {
          this.$set(this.loadingChildrenMap, accountId, false);
        }
      };

      // 1. Partner records for revenue and margins only
      Api.get('/api/admin/partners', {
        params: {
          page_size: allIds.length,
          page_number: 0,
          search_json: searchJson,
        },
      })
        .then((response) => {
          const partners = response.data.data;
          const totalRevenue = partners.reduce(
            (sum, p) => sum + (p.revenue || 0),
            0,
          );
          const totalAbsMargin = partners.reduce(
            (sum, p) => sum + (p.absolute_margin || 0),
            0,
          );
          this.$set(item, 'revenue', totalRevenue);
          this.$set(item, 'absolute_margin', totalAbsMargin);
          this.$set(
            item,
            'relative_margin',
            totalRevenue > 0 ? (totalAbsMargin / totalRevenue) * 100 : 0,
          );
        })
        .catch((e) => console.error('Failed to load partner metrics:', e))
        .finally(done);

      // 2. Job counts from gid-crud — live data, each updates UI independently
      // Rainy potential excluded: distance-based per location, parent value is sufficient
      for (const [endpoint, field] of Object.entries(jobEndpoints)) {
        axios
          .get(`/data-api/entity/job/contact/${accountId}/${endpoint}`, {
            params: { page: 0, page_size: 1, includeChildren: true },
          })
          .then((r) => {
            this.$set(item, field, parseInt(r.data.total) || 0);
          })
          .catch(() => {})
          .finally(done);
      }
    },
    extractPartnerName(cardElement) {
      // Extract partner name from the first cell's first text node
      // The partner name is in a <p> tag that starts with "Partner "
      const nameCell = cardElement.querySelector('td:first-child');
      if (!nameCell) return null;
      const nameParagraph = nameCell.querySelector('p');
      if (!nameParagraph) return null;
      // Get just the first line (partner name) before the address
      const fullText = nameParagraph.textContent?.trim() || '';
      const firstLine = fullText.split('\n')[0]?.trim();
      return firstLine || null;
    },
    selectActiveStatuses() {
      this.isActiveMode = true;
      this.filterStatuses = this.activeStatuses.map((status) => ({
        text: status,
        value: status,
      }));
      this.clearExistingStatusFilters();
    },
    clearAllStatuses() {
      this.isActiveMode = false;
      this.filterStatuses = [];
      this.clearExistingStatusFilters();
    },
    onStatusSelectionChange(selectedValues) {
      this.isActiveMode = false;
      this.filterStatuses = selectedValues || [];
      this.clearExistingStatusFilters();
    },
    clearExistingStatusFilters() {
      if (this.filterAdvancedSearch) {
        if (this.filterAdvancedSearch.and) {
          this.filterAdvancedSearch.and = this.filterAdvancedSearch.and.filter(
            (filter) => filter.field !== 'status',
          );
        }
        if (this.filterAdvancedSearch.or) {
          this.filterAdvancedSearch.or = this.filterAdvancedSearch.or.filter(
            (filter) => filter.field !== 'status',
          );
        }
      }
    },
    filterUpdated() {
      this.currentPage = 1;
      this.$nextTick(() => this.loadItems());
    },
    getSearchParam() {
      const url = new URL(window.location.href);
      const searchParam = url.searchParams.get('search');
      const openModal = url.searchParams.get('openModal');
      const expandDetails = url.searchParams.get('expandDetails');
      let search = null;

      if (searchParam) {
        this.skipWatcher = true;
        this.filterSearch = '';
        search = normalizeSearchInput(searchParam);
        this.$nextTick(() => {
          this.skipWatcher = false;
        });
      }

      return { search, openModal, expandDetails };
    },
    async loadItems() {
      // Clear stale children state — items array is about to be replaced
      this.includeChildrenMap = {};
      this.loadingChildrenMap = {};
      this.originalMetrics = {};

      const { search, openModal, expandDetails } = this.getSearchParam();
      let searchQuery = {};

      if (search) {
        searchQuery = {
          page_size: this.perPage,
          page_number: this.currentPage - 1,
          search,
          sort_dir: this.sortDir ? 'desc' : 'asc',
        };
      } else {
        searchQuery = {
          page_size: this.perPage,
          page_number: this.currentPage - 1,
          search: normalizeSearchInput(this.filterSearch),
          owner: this.filterOwner,
          sort_by: this.sortBy ? this.sortBy : null,
          sort_dir: this.sortDir ? 'desc' : 'asc',
        };
      }
      if (!search) {
        let searchJson = { and: [] };

        if (this.filterStatuses && this.filterStatuses.length > 0) {
          const alreadyExists = Object.keys(
            this.filterAdvancedSearch || {},
          ).some((key) =>
            this.filterAdvancedSearch[key].some((s) => s.field === 'status'),
          );

          if (!alreadyExists) {
            searchJson.and.push({
              field: 'status',
              op: 'in',
              value: this.filterStatuses.map((s) => s.value),
            });
          }
        }

        if (this.filterParentsOnly) {
          searchJson.and.push({
            field: 'is_parent',
            op: 'includes',
            value: true,
          });
        }

        if (this.filterMissingTaxInfo) {
          searchJson.and.push({
            field: 'missing_tax_info',
            op: 'includes',
            value: true,
          });
        }

        if (this.filterUseAdvancedSearch && this.filterAdvancedSearch) {
          if (this.filterAdvancedSearch.and) {
            searchJson.and.push(...this.filterAdvancedSearch.and);
          } else if (this.filterAdvancedSearch.or) {
            const orFilters = this.filterAdvancedSearch.or;
            if (orFilters.length > 0) {
              if (searchJson.and.length > 0) {
                orFilters.unshift(...searchJson.and);
              }
              searchJson = { or: orFilters };
            }
          } else {
            searchJson.and.push(this.filterAdvancedSearch);
          }
        }

        if (
          (searchJson.and && searchJson.and.length > 0) ||
          (searchJson?.or && searchJson.or.length > 0)
        ) {
          searchQuery.search_json = JSON.stringify(searchJson);
        }
      }
      this.loading = true;

      this.items = await Api.get(`/api/admin/partners`, {
        params: searchQuery,
        autoCancelScope: this,
      })
        .then((response) => {
          this.totalRows = response.data.pagination.total_count;
          this.currentPageCount = response.data.data.length;
          this.error = null;
          const partnersData = response.data.data;

          // Store the partners data in the store for state management
          this[SET_PARTNERS_DATA](partnersData);

          return partnersData;
        })
        .catch((error) => {
          this.handleError(error);
          return [];
        })
        .finally(() => {
          this.loading = false;
        });

      if (expandDetails === 'true' && this.items.length) {
        // Opened from the admin jobs table via double-click: show the partner's
        // details inline (expanded row) instead of popping the contacts modal.
        this.$set(this.items[0], '_showDetails', true);
        const url = new URL(window.location.href);
        if (url.searchParams.get('expandDetails')) {
          url.searchParams.delete('expandDetails');
          window.history.replaceState({}, '', url.pathname + url.search);
        }
      } else if (openModal === 'true' && this.items.length) {
        this.onCallResolved(this.items[0], 'potential');
      }
    },
    onCallResolved(partner, tab = 'shown') {
      const {
        account: { id, name },
        completed_jobs,
      } = partner;
      this.jobPartner = {
        id,
        name,
        contacts: this.loadPartnerContacts(partner),
        completedJobs: completed_jobs,
      };
      this.tabToShow = tab;
      this.toggleContactsModal();
    },
    onCallEnded() {
      this.jobPartner = {};
      this.toggleContactsModal();
      const url = new URL(window.location.href);
      if (url.searchParams.get('search')) {
        url.searchParams.delete('search');
        url.searchParams.delete('openModal');
        window.history.replaceState({}, '', url.pathname + url.search);
      }
    },
    loadPartnerContacts(partner) {
      const emailRegex = /(@getitdone.[rocks,co])\w+/g;

      return partner.contacts
        .filter(
          (contact) =>
            contact &&
            contact.profiles.length > 0 &&
            (contact.phone || contact.mobile_phone) &&
            !emailRegex.test(contact?.email),
        )
        .map((item) => ({
          firstName: item.first_name,
          lastName: item.last_name,
          phone: item.phone,
          mobilePhone: item.mobile_phone,
          id: item.api_id,
          email: item.email,
        }));
    },
    toggleContactsModal() {
      this.showJobPartnerModal = !this.showJobPartnerModal;
    },
    isPartnerWatching(partner) {
      return partner.watchlist.some(
        (elem) => elem.sfid === this.$store.state.auth.user.contact.sfid,
      );
    },
    toggleWatching(partner) {
      const isWatching = this.isPartnerWatching(partner);
      if (isWatching) {
        this.unWatchPartner(partner, this.$store.state.auth.user.contact.sfid);
      } else {
        this.watchPartner(partner);
      }
    },
    async watchPartner(partner) {
      this.loading = true;
      axios
        .post(`/data-api/partner-watchlist`, {
          partnerId: partner.account.id,
        })
        .catch((error) => {
          this.handleError(error);
        });
      this.loadItems();
    },
    async unWatchPartner(partner, adminId) {
      this.loading = true;
      axios
        .post(`/data-api/partner-watchlist/unwatch`, {
          partnerId: partner.account.id,
          adminId,
        })
        .catch((error) => {
          this.handleError(error);
        });
      this.loadItems();
    },
    handleError(error) {
      const err = { details: error };
      if (error.response) {
        err.message = error.response.data.msg || error.response.data;
      } else if (error.request) {
        err.message = this.$t('_errors.network.details');
      } else {
        err.message = error.message;
      }
      this.error = err;
    },
  },
};
</script>
<style scoped lang="scss">
.contact-partner-btn-content {
  display: inline-block;
  margin-right: 0.5rem;

  & > .left-icon {
    font-size: 1.1rem;
    vertical-align: sub;
    margin-right: 0.5rem;
  }
}
.cursor-pointer {
  cursor: pointer;
}
.text-ellipsis {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
.heading-2 {
  font-size: 2rem;
}
.heading-3 {
  font-size: 1rem;
}
::v-deep .menu-styling {
  width: 250px;
  padding: 0.5rem;
}

.status-button-group {
  display: flex;
  align-items: stretch;
}

.status-button {
  height: 38px !important;
  min-height: 38px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 12px !important;
  border-radius: 0 !important;
}

.status-button:first-child {
  border-top-left-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
}

.status-button:last-child {
  border-top-right-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
}
</style>
